/**
 * #1165 - MCP quota-wall context for the pricing page.
 *
 * The backend quota-wall error links to /account-subscription with
 * `mcp_surface=mcp`, `mcp_journey_id` and `mcp_client` (api-rate-limit-error.ts). This
 * reads those params so the page can report `mcp_pricing_shown` against the journey and
 * show the MCP-specific intro. Pure: no I/O, no React.
 */

export interface McpPricingContext {
  journeyId?: string;
  client?: string;
}

export type McpPricingArm = 'control' | 'treatment';

// Router query values are loosely typed (string, string[], nested parsed objects); anything
// that is not a string or a string array is ignored.
type Query = Record<string, unknown>;

// Opaque ids only: bounded and restricted to a safe charset, because the values come
// from the URL and are echoed into an analytics event.
const SAFE_ID = /^[A-Za-z0-9_.:-]{1,64}$/;

const first = (v: unknown): string | undefined => {
  const raw = Array.isArray(v) ? v[0] : v;
  return typeof raw === 'string' ? raw : undefined;
};

const safeId = (v: unknown): string | undefined => {
  const s = first(v);
  return s !== undefined && SAFE_ID.test(s) ? s : undefined;
};

/** Returns the context only when the visitor arrived from the hosted-MCP quota wall. */
export function parseMcpPricingContext(query: Query): McpPricingContext | null {
  if (first(query.mcp_surface) !== 'mcp') return null;
  return { journeyId: safeId(query.mcp_journey_id), client: safeId(query.mcp_client) };
}

/** Anything other than an explicit 'treatment' is control, so a failed lookup never shows new copy. */
export function toMcpPricingArm(raw: unknown): McpPricingArm {
  if (typeof raw === 'object' && raw !== null && 'arm' in raw && raw.arm === 'treatment') return 'treatment';
  return 'control';
}
